import MaterialIcon from '@components/common/MaterialIcon';
import { Spinner } from '@components/common/Spinner';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import {
  PROVISIONING_PROXY_MAPPING_STEP,
  PROVISIONING_STEP_CATALOG,
  type TProvisioningStatus,
} from '@conversed/shared/src/central/types/provisioning.type';
import type { TProvisionStepView } from '@hooks/provisioning/useProvisioningStatus';
import type React from 'react';
import { useTranslation } from 'react-i18next';

// * The same curtain drives both directions: provisioning walks the steps
// * forward and ends on 'done', a superAdmin teardown walks them backward and
// * ends on 'rolled_back'.
type TCurtainMode = 'provisioning' | 'teardown';

const STEP_LABEL_KEY_BY_NUMBER = new Map<number, string>(
  PROVISIONING_STEP_CATALOG.map((entry) => [entry.step, entry.key]),
);

const resolveMode = (status: TProvisioningStatus | null): TCurtainMode => {
  if (
    status === 'deprovisioning' ||
    status === 'deprovisioned' ||
    status === 'deprovision_failed'
  ) {
    return 'teardown';
  }

  return 'provisioning';
};

const getStepLabelKey = (stepNumber: number): string => {
  if (stepNumber === PROVISIONING_PROXY_MAPPING_STEP) {
    return 'provisioning.step.proxy-mapping';
  }

  const key = STEP_LABEL_KEY_BY_NUMBER.get(stepNumber);

  if (!key) {
    return 'provisioning.step.unknown';
  }

  return `provisioning.step.${key}`;
};

const getRetryLabelKey = (mode: TCurtainMode): string => {
  if (mode === 'teardown') {
    return 'provisioning.button.retry-teardown';
  }

  return 'provisioning.button.retry';
};

const getPendingStepMarker = (): React.ReactNode => (
  <div className='w-4 h-4 rounded-full border border-gray-300 flex-shrink-0' />
);

const getProvisioningStepIcon = (
  stepView: TProvisionStepView | undefined,
): React.ReactNode => {
  if (!stepView) {
    return getPendingStepMarker();
  }

  if (stepView.status === 'done') {
    return (
      <MaterialIcon
        icon='check_circle'
        classes={['text-green-500', 'text-base', 'flex-shrink-0']}
      />
    );
  }

  if (stepView.status === 'pending') {
    return <Spinner size='sm' />;
  }

  if (stepView.status === 'failed') {
    return (
      <MaterialIcon
        icon='error'
        classes={['text-red-500', 'text-base', 'flex-shrink-0']}
      />
    );
  }

  if (stepView.status === 'rollback_failed') {
    return (
      <MaterialIcon
        icon='warning'
        classes={['text-red-500', 'text-base', 'flex-shrink-0']}
      />
    );
  }

  // * rolled_back: a completed step undone after a later failure.
  return (
    <MaterialIcon
      icon='undo'
      classes={['text-gray-400', 'text-base', 'flex-shrink-0']}
    />
  );
};

const getTeardownStepIcon = (
  stepView: TProvisionStepView | undefined,
): React.ReactNode => {
  // * A step still marked 'done' is one the unwind has not reached yet.
  if (!stepView || stepView.status === 'done') {
    return getPendingStepMarker();
  }

  if (stepView.status === 'rolled_back') {
    return (
      <MaterialIcon
        icon='check_circle'
        classes={['text-green-500', 'text-base', 'flex-shrink-0']}
      />
    );
  }

  if (stepView.status === 'skipped') {
    return (
      <MaterialIcon
        icon='lock'
        classes={['text-amber-500', 'text-base', 'flex-shrink-0']}
      />
    );
  }

  if (stepView.status === 'rollback_failed' || stepView.status === 'failed') {
    return (
      <MaterialIcon
        icon='warning'
        classes={['text-red-500', 'text-base', 'flex-shrink-0']}
      />
    );
  }

  return <Spinner size='sm' />;
};

const getProvisioningStepTextColor = (
  stepView: TProvisionStepView | undefined,
): string => {
  if (!stepView) {
    return 'text-gray-400';
  }

  if (stepView.status === 'failed' || stepView.status === 'rollback_failed') {
    return 'text-red-600';
  }

  if (stepView.status === 'rolled_back') {
    return 'text-gray-400';
  }

  if (stepView.status === 'done') {
    return 'text-gray-700';
  }

  return 'text-gray-500';
};

const getTeardownStepTextColor = (
  stepView: TProvisionStepView | undefined,
): string => {
  if (!stepView || stepView.status === 'done') {
    return 'text-gray-400';
  }

  if (stepView.status === 'failed' || stepView.status === 'rollback_failed') {
    return 'text-red-600';
  }

  if (stepView.status === 'skipped') {
    return 'text-amber-600';
  }

  return 'text-gray-700';
};

interface ProvisioningCurtainProps {
  status: TProvisioningStatus | null;
  stepStates: Map<number, TProvisionStepView>;
  identifier?: string;
  canRetry: boolean;
  isRetrying: boolean;
  isNavigatingToCompany: boolean;
  onGoToCompany: () => void;
  onRetry: () => void;
  onBack: () => void;
  onClose: () => void;
}

export const ProvisioningCurtain: React.FC<ProvisioningCurtainProps> = ({
  status,
  stepStates,
  identifier,
  canRetry,
  isRetrying,
  isNavigatingToCompany,
  onGoToCompany,
  onRetry,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation();
  const mode = resolveMode(status);

  const getStatusIcon = (): React.ReactNode => {
    if (status === 'completed' || status === 'deprovisioned') {
      return (
        <MaterialIcon
          icon='check_circle'
          classes={['text-green-500', 'text-5xl']}
        />
      );
    }

    if (status === 'failed') {
      return (
        <MaterialIcon icon='error' classes={['text-red-500', 'text-5xl']} />
      );
    }

    if (status === 'deprovision_failed') {
      return (
        <MaterialIcon icon='warning' classes={['text-red-500', 'text-5xl']} />
      );
    }

    if (status === 'in_progress' || status === 'deprovisioning') {
      return <Spinner size='lg' />;
    }

    return (
      <MaterialIcon icon='cloud_off' classes={['text-gray-400', 'text-5xl']} />
    );
  };

  const getTitle = (): string => {
    if (status === 'completed') {
      return t('provisioning.title.completed');
    }

    if (status === 'failed') {
      return t('provisioning.title.failed');
    }

    if (status === 'in_progress') {
      return t('provisioning.title.in-progress');
    }

    if (status === 'deprovisioning') {
      return t('provisioning.title.deprovisioning');
    }

    if (status === 'deprovisioned') {
      return t('provisioning.title.deprovisioned');
    }

    if (status === 'deprovision_failed') {
      return t('provisioning.title.deprovision-failed');
    }

    return t('provisioning.title.not-provisioned');
  };

  const getStepIcon = (
    stepView: TProvisionStepView | undefined,
  ): React.ReactNode => {
    if (mode === 'teardown') {
      return getTeardownStepIcon(stepView);
    }

    return getProvisioningStepIcon(stepView);
  };

  const getStepTextColor = (
    stepView: TProvisionStepView | undefined,
  ): string => {
    if (mode === 'teardown') {
      return getTeardownStepTextColor(stepView);
    }

    return getProvisioningStepTextColor(stepView);
  };

  const getRowStepNumbers = (): number[] => {
    const catalogStepNumbers = PROVISIONING_STEP_CATALOG.map(
      (entry) => entry.step,
    );
    const hasProxyStep = stepStates.has(PROVISIONING_PROXY_MAPPING_STEP);
    const stepNumbers = hasProxyStep
      ? [...catalogStepNumbers, PROVISIONING_PROXY_MAPPING_STEP]
      : catalogStepNumbers;

    if (mode === 'teardown') {
      return [...stepNumbers].reverse();
    }

    return stepNumbers;
  };

  const rowStepNumbers = getRowStepNumbers();

  return (
    <div className='absolute inset-0 z-20 bg-white flex items-center justify-center overflow-hidden'>
      {status === 'completed' && (
        <div className='absolute top-0 left-0 m-2'>
          <MaterialIcon
            icon='close'
            onClick={onClose}
            tooltip={t('provisioning.button.close')}
          />
        </div>
      )}
      <div className='w-full max-w-[600px] px-4 my-8 flex flex-col max-h-[calc(100%-4rem)]'>
        <div className='flex justify-center mb-4 flex-shrink-0'>
          {getStatusIcon()}
        </div>

        <h2 className='text-center text-lg font-semibold text-gray-900 mb-1 flex-shrink-0'>
          {getTitle()}
        </h2>

        {identifier && (
          <p className='text-center text-tiny text-gray-400 mb-5 flex-shrink-0'>
            {identifier}
          </p>
        )}

        {status === 'deprovisioned' && (
          <p className='text-center text-tiny text-gray-500 flex-shrink-0'>
            {t('provisioning.note.deprovisioned')}
          </p>
        )}

        <div className='border border-gray-100 rounded-xl px-4 py-3 mb-4 space-y-2 bg-gray-50 flex-1 min-h-0 overflow-y-auto mt-3'>
          {rowStepNumbers.map((stepNumber) => {
            const stepView = stepStates.get(stepNumber);

            return (
              <div key={stepNumber} className='flex items-center gap-3'>
                {getStepIcon(stepView)}
                <span className={`text-tiny ${getStepTextColor(stepView)}`}>
                  {t(getStepLabelKey(stepNumber))}
                </span>
                {stepView?.status === 'skipped' && (
                  <span className='text-tiny text-amber-600'>
                    {t('provisioning.step-status.skipped')}
                  </span>
                )}
                {stepView?.error && (
                  <span className='text-tiny text-red-500 truncate'>
                    {stepView.error}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className='flex flex-col items-center gap-3 flex-shrink-0'>
          {status === 'completed' && (
            <ButtonT
              type='button'
              styling='primary'
              rounding='md'
              size='xs'
              disabled={isNavigatingToCompany}
              onClick={onGoToCompany}>
              {t('provisioning.button.go-to-company')}
            </ButtonT>
          )}

          {canRetry && (
            <ButtonT
              type='button'
              styling='primary'
              rounding='md'
              size='xs'
              disabled={isRetrying}
              onClick={onRetry}>
              {t(getRetryLabelKey(mode))}
            </ButtonT>
          )}

          {status !== 'completed' && (
            <button
              type='button'
              onClick={onBack}
              className='text-tiny text-gray-400 hover:text-gray-600 transition-colors font-normal'>
              {t('provisioning.button.back-to-customers')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
